import React from "react";

import { compareDesc } from "date-fns";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import AddressComponent, {
  LocationExtensionField,
} from "../../../../../../components/Generic/AddressComponent";
import GeneralCommentsComponent from "../../../../../../components/Generic/GeneralComments";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../context/StateContext";
import { useCustomerHook } from "../../../../../../hooks/useCustomerHook";
import { useOrderContext } from "../../../../../../hooks/useOrderContext";
import { AutomaticSequencing } from "../../../../../../models/automatic.sequencing";
import { GeneralComments } from "../../../../../../models/general.comments";
import { CustomerLocations, QuoteComment } from "../../../../../../models/sales";
import { User } from "../../../../../../models/user";
import SkeletonDetails from "../skeleton";
import FormComponent from "./FormComponent";
import useStyles from "./styles";

interface Props {
  customerDestinationErrors?: yup.ValidationError[];
  customerOriginErrors?: yup.ValidationError[];
  errors?: yup.ValidationError[];
  onSave: () => void;
  onSavePublish?: () => void;
  onCancel: () => void;
  onChange?: (noChanges?: boolean) => void;
  isUpdating: boolean;
  sequence?: AutomaticSequencing;
  isEdit?: boolean;
  externalList?: ExternalList;
  contactsErrors?: {
    origin: yup.ValidationError[][];
    destination: yup.ValidationError[][];
  };
  usersLoading?: boolean;
  allUsers?: User[];
  showActionButtons?: boolean;
  showGeneralComments?: boolean;
  compact?: boolean;
}

const DetailsTab = ({
  customerDestinationErrors,
  customerOriginErrors,
  errors = [],
  onSave,
  onSavePublish,
  onCancel,
  onChange,
  isUpdating = false,
  sequence,
  isEdit = false,
  externalList,
  contactsErrors,
  usersLoading,
  allUsers,
  showActionButtons = true,
  showGeneralComments = true,
  compact = false,
}: Props) => {
  const classes = useStyles();
  const { order, setOrder, handleUpdateOrder } = useOrderContext();
  const { customer } = useCustomerHook();
  const { currentUser } = useAppState();

  const isMilitaryCompany = Boolean(
    order?.company?.isMilitary ?? currentUser?.company?.isMilitary
  );

  const { t: translate } = useTranslation();

  const updateOrder = (attribute: string, value: any, noChanges?: boolean) => {
    if (handleUpdateOrder) {
      if (onChange) onChange(noChanges);

      if (attribute === "all" && !isEdit && customer) {
        handleUpdateOrder(attribute, {
          ...value,
          origin: { customer },
          destination: { customer },
        });

        return;
      }

      handleUpdateOrder(attribute, value);
    }
  };

  const checkIfDestinationCustomerIsEmpty = () => {
    if (
      !order?.destination?.customer?.name &&
      (!order?.destination?.customer?.email || !order?.destination?.customer?.phone) &&
      !order?.destination?.customer?.destinationMobile
    )
      return true;

    return false;
  };

  const pickupLocationExtensionKey = (field: LocationExtensionField): string => {
    const map: Record<LocationExtensionField, string> = {
      baseCode: "pickupBaseCode",
      baseDescription: "pickupBaseDescription",
      areaCode: "pickupAreaCode",
      areaDescription: "pickupAreaDescription",
      agentName: "pickupAgentName",
      agentZip: "pickupAgentZip",
      agentCity: "pickupAgentCity",
    };

    return map[field];
  };

  const deliveryLocationExtensionKey = (field: LocationExtensionField): string => {
    const map: Record<LocationExtensionField, string> = {
      baseCode: "deliveryBaseCode",
      baseDescription: "deliveryBaseDescription",
      areaCode: "deliveryAreaCode",
      areaDescription: "deliveryAreaDescription",
      agentName: "deliveryAgentName",
      agentZip: "deliveryAgentZip",
      agentCity: "deliveryAgentCity",
    };

    return map[field];
  };

  const handleAddComment = (comment: GeneralComments) => {
    const clonnedComments = order?.quoteComments || [];
    const newComment = {
      ...comment,
      jobId: order?.jobId,
    } as QuoteComment;

    clonnedComments.unshift(newComment);

    setOrder({ ...order, quoteComments: clonnedComments });
  };

  return (
    <div>
      <div className={compact ? classes.compactContainer : classes.container}>
        <FormComponent
          order={order}
          errors={errors}
          update={updateOrder}
          sequence={sequence}
          externalList={externalList}
          isFetching={usersLoading}
          ordersUsers={allUsers}
        />

        <div style={{ marginTop: "15px" }} />

        <AddressComponent
          title={translate("pickup")}
          {...(isMilitaryCompany
            ? {
                locationExtension: {
                  baseCode: order?.pickupBaseCode,
                  baseDescription: order?.pickupBaseDescription,
                  areaCode: order?.pickupAreaCode,
                  areaDescription: order?.pickupAreaDescription,
                  agentName: order?.pickupAgentName,
                  agentZip: order?.pickupAgentZip,
                  agentCity: order?.pickupAgentCity,
                },
                onLocationExtensionChange: (field: LocationExtensionField, value: string) =>
                  updateOrder(pickupLocationExtensionKey(field), value),
              }
            : {})}
          update={(value: CustomerLocations) => {
            if (
              value?.customer?.id === order?.destination?.customer?.id ||
              checkIfDestinationCustomerIsEmpty()
            ) {
              updateOrder("all", {
                ...order,
                origin: {
                  customer: value.customer,
                  contacts: value.contacts,
                  locations: value.locations,
                },
                destination: {
                  customer: value.customer,
                  contacts: order?.destination?.contacts,
                  locations: order?.destination?.locations,
                },
              });
            } else {
              updateOrder("origin", value);
            }
          }}
          inventory={order?.inventory || []}
          access={order?.pickupAccess}
          customerLocations={order?.origin || ({} as CustomerLocations)}
          updateAll={(inventory, customerLocation) =>
            setOrder({
              ...order,
              inventory,
              origin: customerLocation,
            })
          }
          onCopyInfo={() =>
            updateOrder("origin", {
              customer: order?.destination?.customer,
              locations: order?.origin?.locations,
              contacts: order?.origin?.contacts,
            })
          }
          externalList={externalList}
          externalErrors={customerOriginErrors}
          contactsErrors={contactsErrors?.origin}
        />

        <AddressComponent
          title={translate("delivery")}
          {...(isMilitaryCompany
            ? {
                locationExtension: {
                  baseCode: order?.deliveryBaseCode,
                  baseDescription: order?.deliveryBaseDescription,
                  areaCode: order?.deliveryAreaCode,
                  areaDescription: order?.deliveryAreaDescription,
                  agentName: order?.deliveryAgentName,
                  agentZip: order?.deliveryAgentZip,
                  agentCity: order?.deliveryAgentCity,
                },
                onLocationExtensionChange: (field: LocationExtensionField, value: string) =>
                  updateOrder(deliveryLocationExtensionKey(field), value),
              }
            : {})}
          inventory={order?.inventory || []}
          access={order?.deliveryAccess}
          customerLocations={order?.destination || ({} as CustomerLocations)}
          update={(value: CustomerLocations) => {
            updateOrder("all", {
              ...order,
              destination: value,
              origin: {
                ...order?.origin,
                customer: {
                  ...order?.origin?.customer,
                  destinationMobile: value.customer.destinationMobile,
                },
              },
            });
          }}
          updateAll={(inventory, customerLocation) => {
            setOrder({
              ...order,
              inventory,
              destination: customerLocation,
            });
          }}
          onCopyInfo={() => {
            updateOrder("destination", {
              customer: order?.origin?.customer,
              locations: order?.destination?.locations,
              contacts: order?.origin?.contacts,
            });
          }}
          externalList={externalList}
          externalErrors={customerDestinationErrors}
          contactsErrors={contactsErrors?.destination}
        />

        {showGeneralComments && (
          <GeneralCommentsComponent
            comments={order?.quoteComments?.sort((a, b) =>
              compareDesc(new Date(a.createdAt ?? ""), new Date(b.createdAt ?? ""))
            )}
            onAddComment={handleAddComment}
            onRemoveComment={(comments: QuoteComment[]) =>
              setOrder({ ...order, quoteComments: comments })
            }
          />
        )}

        {showActionButtons && (
          <div className={classes.buttons}>
            <ActionButtons
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              onCancel={onCancel}
              onSavePublish={!isUpdating && onSavePublish ? () => onSavePublish() : undefined}
              onSave={onSave}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export { SkeletonDetails };
export default DetailsTab;
